import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { QrCode, CheckCircle2 } from "lucide-react";
import { api, fmtErr } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const inputCls =
    "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-4">
            <QrCode size={26} />
          </span>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Asset Passport</h1>
        </div>

        <div className="bg-white rounded-3xl border border-zinc-200/80 card-shadow p-7 sm:p-8" data-testid="reset-password-page">
          {!token ? (
            <p className="text-sm text-zinc-500 text-center py-4" data-testid="reset-invalid-token">
              Este enlace no es válido. Solicita uno nuevo desde la pantalla de acceso.
            </p>
          ) : done ? (
            <div className="text-center py-4">
              <span className="h-14 w-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={24} />
              </span>
              <h2 className="font-heading text-xl font-semibold tracking-tight">Contraseña actualizada</h2>
              <p className="text-sm text-zinc-500 mt-3" data-testid="reset-success-message">
                Ya puedes iniciar sesión con tu nueva contraseña. Redirigiendo…
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl font-semibold tracking-tight mb-2">Nueva contraseña</h2>
              <p className="text-sm text-zinc-500 mb-6">Elige una contraseña de al menos 6 caracteres.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Nueva contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  autoComplete="new-password"
                  data-testid="reset-password-input"
                />
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Repite la contraseña"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="reset-confirm-input"
                />
                {error && (
                  <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="reset-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
                >
                  {loading ? "Guardando…" : "Guardar nueva contraseña"}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="reset-back-login-link">
            Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
